/**
 * Next Step Club launcher: floating button + side panel (snippets/loyalty-launcher.liquid).
 *
 * The logged-in customer's balance is fetched from the app proxy
 * (/apps/loyalty/me → next-table) the first time the panel opens, never on
 * page load. Everything coming from the server is written with textContent.
 *
 * Redeeming (POST /apps/loyalty/redeem) turns Steps into a one-off discount
 * code for this customer, then puts it in the cart. The birthday is saved once
 * (POST /apps/loyalty/birthday) and is locked afterwards: only support can
 * change it. Members the referral program is open to get their invite link
 * (on the shop's primary domain, data-shop-url) with copy / share buttons.
 * The panel is re-rendered from `this.data` + `this.state` after every change.
 * Any [data-loyalty-open] element opens it; data-loyalty-open="rewards" /
 * "refer", or ?club=rewards / ?club=refer on any page, open it scrolled there.
 *
 * "Earn more Steps" (only when /me sends member.earn): the welcome bonus is
 * given by /me itself the first time (response `welcome`, shown once), the
 * profile (shoe size + styles) posts to /apps/loyalty/profile and following
 * us posts to /apps/loyalty/social while the link opens in a new tab.
 *
 * The desktop bubble ("You have €10 to use") and the mobile menu dot come
 * from Liquid (data-bubble-value: the biggest reward the synced balance buys);
 * dismissing it or opening the panel hides them for the rest of the session.
 *
 * A blocked member (chargeback / breach of the terms) gets only a notice:
 * Liquid renders it straight away when the customer metafield says so (no
 * [data-loyalty-member], so no request), and /me answering `blocked: true`
 * swaps it in on any later render. Same for a member who left the Club
 * (`left: true`, the [data-loyalty-left] notice).
 *
 * "Club emails" (above the leave link): on / off, posted to
 * /apps/loyalty/emails. Off keeps the membership and the Steps; next-table
 * still sends the reward codes and the expiry reminders.
 *
 * "Leave Next Step Club" sits at the bottom of the panel (data-loyalty-open=
 * "leave" scrolls there): a dialog over the panel lists what is lost and
 * offers turning the emails off instead, then posts to /apps/loyalty/leave.
 * Next-table deletes the member's Club data; here the panel shows the "you
 * left" notice and the Steps count, the cart's "Use my Steps" and the bubble
 * go away. The notice has "Rejoin Next Step Club" (not for someone who was
 * blocked when they left): it asks, posts to /apps/loyalty/rejoin and loads
 * the new membership, which starts from zero.
 *
 * /me failing or taking over 10 s: the balance and level Liquid knows (synced
 * metafields, data-synced-*) with a note that rewards and activity can't load.
 *
 * After a redeem the header / menu count ([data-loyalty-steps]) and the cart's
 * "Use my Steps" ([data-loyalty-cart], hidden below its data-min-points) still
 * show the synced metafield: the new balance is kept in sessionStorage and put
 * back after every re-render, until a page renders a different synced number.
 */
if (!customElements.get('loyalty-panel')) {
  // Server error code → translation key. Anything else: "try again".
  const REDEEM_ERRORS = {
    not_enough_points: 'err_points',
    // Expired by time but not yet written by the hourly job: to the customer, not enough Steps.
    expired: 'err_points',
    not_open: 'err_closed',
    not_member: 'err_member',
    login: 'err_login',
  };
  const BIRTHDAY_ERRORS = {
    invalid_date: 'bday_err_invalid',
    locked: 'bday_err_locked',
    not_member: 'bday_err_member',
    login: 'err_login',
  };
  // open(section) / ?club=section → the panel block to scroll to.
  const SCROLL_TARGETS = new Map([
    ['rewards', '.loyalty-launcher__rewards'],
    ['refer', '.loyalty-launcher__refer'],
    ['leave', '.loyalty-launcher__leave'],
  ]);
  // Same keys / sizes as next-table shared loyalty/earn.ts.
  const PROFILE_STYLES = ['sneakers', 'streetwear', 'accessories', 'watches', 'collectibles'];
  const SHOE_SIZES = Array.from({ length: 28 }, (_, i) => String(35 + i / 2));
  const SOCIAL_PLATFORMS = ['instagram', 'tiktok'];
  const ACTIVITY_LABELS = {
    expire: 'act_expire',
    redeem: 'act_redeem',
    birthday: 'act_birthday',
    referral: 'act_referral',
    signup: 'act_signup',
    profile: 'act_profile',
    social: 'act_social',
  };
  const BUBBLE_SEEN_KEY = 'loyalty-bubble-seen';
  const BALANCE_KEY = 'loyalty-balance';
  const MIN_BIRTHDAY = '1900-01-01';
  const pad = (n) => String(n).padStart(2, '0');
  const RETRY_DELAY = 1500;
  const MAX_RETRIES = 2;
  const ME_TIMEOUT = 10000;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  // The server only takes a UUID. randomUUID needs a secure context and a recent browser;
  // otherwise an RFC 4122 version 4 UUID from getRandomValues.
  const newKey = () => {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (n) => n.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };

  class LoyaltyPanel extends HTMLElement {
    connectedCallback() {
      // Moved in the DOM (re-inserted): the listeners below are already in place.
      if (this.started) return;
      this.started = true;
      // The Liquid `t` filter HTML-escapes strings (' → &#39;); decode once, they're rendered with textContent.
      const strings = (window.theme && window.theme.translations && window.theme.translations.loyalty) || {};
      const decoder = document.createElement('textarea');
      this.t = Object.fromEntries(
        Object.entries(strings).map(([key, value]) => {
          decoder.innerHTML = value;
          return [key, decoder.value];
        }),
      );
      const locale = this.dataset.locale || document.documentElement.lang || undefined;
      // European store: "20 October 2026", not the US "October 20, 2026".
      this.locale = locale === 'en' ? 'en-GB' : locale;
      this.button = this.querySelector('.loyalty-launcher__button');
      this.panel = this.querySelector('.loyalty-launcher__panel');
      this.overlay = this.querySelector('.loyalty-launcher__overlay');
      this.body = this.querySelector('.loyalty-launcher__body');
      this.member = this.querySelector('[data-loyalty-member]');
      this.loaded = false;
      this.data = null;
      // confirmId: reward whose confirmation is open; key: its idempotency key
      // (kept across retries of the same confirmation); cart: code → add-to-cart status.
      this.state = { confirmId: null, key: null, busy: false, error: null, result: null, cart: {} };
      // Birthday form: confirm = the date awaiting confirmation; saved = the POST response.
      this.bday = { confirm: null, busy: false, error: null, saved: null };
      // Earn more Steps: profile form state, follow clicks, and the one-time welcome from /me.
      this.profile = { editing: false, busy: false, error: null, awarded: 0 };
      this.social = { busy: {}, error: {}, awarded: {} };
      // Leaving the Club: confirm = the dialog is open; error = translation key.
      this.leave = { confirm: false, busy: false, error: null };
      // Club emails switch, and rejoining from the "you left" notice.
      this.emails = { busy: false, error: null };
      this.rejoin = { confirm: false, busy: false, error: null };
      this.leftNotice = this.querySelector('[data-loyalty-left]');
      if (this.leftNotice) this.leftNotice.addEventListener('click', (e) => this.onRejoinAction(e));
      this.welcome = null;
      this.onKey = (e) => {
        // The leave dialog closes first, the panel after.
        if (e.key === 'Escape') {
          if (this.leave.confirm) this.openLeave(false);
          else this.close();
        } else if (e.key === 'Tab') this.keepFocusInside(e);
      };

      this.button.addEventListener('click', () => this.open());
      // Phones / tablets: the floating button is hidden; the mobile menu's
      // "Next Step Club" row (data-loyalty-open) opens the panel instead.
      // data-loyalty-open="rewards" (cart, Club page) opens it on the rewards.
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-loyalty-open]');
        if (opener) this.open(opener.dataset.loyaltyOpen);
      });
      this.overlay.addEventListener('click', () => this.close());
      this.querySelectorAll('[data-loyalty-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      if (this.member) this.member.addEventListener('click', (e) => this.onAction(e));
      this.setupBubble();
      this.restoreBalance();

      // Come back to this page after signing in.
      const login = this.querySelector('[data-loyalty-login]');
      if (login) {
        const url = new URL(login.getAttribute('href'), window.location.origin);
        url.searchParams.set('return_url', window.location.pathname + window.location.search);
        login.setAttribute('href', url.pathname + url.search);
      }

      // ?club=rewards (the account page links to it): open on the rewards, then
      // drop the parameter like the referral popup drops ?ref=. The sign-in
      // link above keeps it, so a guest lands back on the rewards after signing in.
      const params = new URLSearchParams(window.location.search);
      if (params.has('club')) {
        this.open(params.get('club'));
        params.delete('club');
        const query = params.toString();
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      }
    }

    // section "rewards" / "refer": scroll the panel there once it's rendered.
    open(section) {
      this.scrollTarget = SCROLL_TARGETS.get(section) || null;
      if (this.classList.contains('is-open')) {
        this.scrollToTarget();
        return;
      }
      // First: opened from the bubble, focus moves to the launcher button, which then gets it back on close.
      this.dismissBubble();
      this.opener = document.activeElement;
      this.panel.hidden = false;
      this.overlay.hidden = false;
      // Lay the panel out off-screen first, so adding is-open slides it in.
      void this.panel.offsetWidth;
      requestAnimationFrame(() => this.classList.add('is-open'));
      document.body.classList.add('loyalty-open');
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.onKey);
      this.panel.querySelector('[data-loyalty-close]').focus();
      // "Rejoin" on the Club page: straight to the question in the "you left" notice.
      if (section === 'rejoin' && this.leftNotice && !this.leftNotice.hidden) this.openRejoin(true);
      if (this.member && !this.loaded) this.load();
      else this.scrollToTarget();
    }

    // A modal dialog: Tab from the last control goes back to the first, Shift+Tab the other way.
    // While the leave dialog is open, inside that dialog.
    keepFocusInside(e) {
      const scope = this.modalEl || this.panel;
      const focusable = [...scope.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
        (el) => !el.closest('[hidden]') && el.getClientRects().length > 0,
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!scope.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // Guests (no rewards list) and a failed load just open at the top.
    scrollToTarget() {
      const target = this.scrollTarget && this.body.querySelector(this.scrollTarget);
      this.scrollTarget = null;
      if (target) this.body.scrollTop += target.getBoundingClientRect().top - this.body.getBoundingClientRect().top;
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.body.classList.remove('loyalty-open');
      this.button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onKey);
      setTimeout(() => {
        if (this.classList.contains('is-open')) return;
        this.panel.hidden = true;
        this.overlay.hidden = true;
      }, 300);
      (this.opener && this.opener.isConnected ? this.opener : this.button).focus();
      // Start clean next time (a request still running keeps its state).
      if (this.data && !this.state.busy) Object.assign(this.state, { confirmId: null, key: null, error: null, result: null });
      if (this.data && !this.bday.busy) Object.assign(this.bday, { confirm: null, error: null });
      if (this.data && !this.profile.busy) Object.assign(this.profile, { editing: false, error: null, awarded: 0 });
      if (!this.leave.busy) this.leave = { confirm: false, busy: false, error: null };
      if (!this.emails.busy) this.emails.error = null;
      if (!this.rejoin.busy && this.rejoin.confirm) {
        this.rejoin = { confirm: false, busy: false, error: null };
        this.renderRejoin();
      }
      this.welcome = null;
      this.social.error = {};
      this.social.awarded = {};
      if (this.data) this.render();
    }

    // The desktop bubble and the mobile menu dot: until dismissed or the panel opens, once per session.
    setupBubble() {
      const value = Number(this.dataset.bubbleValue || 0);
      if (!(value > 0) || this.bubbleSeen()) return;
      const bubble = this.querySelector('[data-loyalty-bubble]');
      if (bubble) {
        bubble.querySelector('[data-loyalty-bubble-text]').textContent = this.fmt(this.t.bubble, { amount: this.money(value) });
        bubble.querySelector('[data-loyalty-bubble-close]').addEventListener('click', () => this.dismissBubble());
        bubble.hidden = false;
      }
      document.querySelectorAll('[data-loyalty-bubble-dot]').forEach((dot) => {
        dot.hidden = false;
      });
    }

    bubbleSeen() {
      try {
        return window.sessionStorage.getItem(BUBBLE_SEEN_KEY) === '1';
      } catch (err) {
        return false;
      }
    }

    dismissBubble() {
      try {
        window.sessionStorage.setItem(BUBBLE_SEEN_KEY, '1');
      } catch (err) {
        /* private mode: hidden for this page only */
      }
      const bubble = this.querySelector('[data-loyalty-bubble]');
      if (bubble && !bubble.hidden) {
        // Focus would be lost with it: the launcher button sits right below.
        if (bubble.contains(document.activeElement)) this.button.focus();
        bubble.hidden = true;
      }
      document.querySelectorAll('[data-loyalty-bubble-dot]').forEach((dot) => {
        dot.hidden = true;
      });
    }

    // After a redeem: the new balance for the header / menu count and the cart block, which
    // Liquid renders from the synced metafield. `before` is that metafield on this page.
    saveBalance(points) {
      try {
        window.sessionStorage.setItem(BALANCE_KEY, JSON.stringify({ before: this.dataset.syncedPoints, points }));
      } catch (err) {
        /* private mode: this page only */
      }
      this.balancePoints = points;
      this.applyBalance();
      this.watchBalance();
    }

    // Later pages this session: kept while the metafield still shows what it showed at the
    // redeem; any other number means it synced (or moved on), so Liquid is right again.
    restoreBalance() {
      let saved = null;
      try {
        saved = JSON.parse(window.sessionStorage.getItem(BALANCE_KEY));
      } catch (err) {
        return;
      }
      if (!saved) return;
      if (this.dataset.syncedPoints === undefined || saved.before !== this.dataset.syncedPoints) {
        try {
          window.sessionStorage.removeItem(BALANCE_KEY);
        } catch (err) {
          /* nothing to clean */
        }
        return;
      }
      this.balancePoints = saved.points;
      this.applyBalance();
      this.watchBalance();
    }

    // Only writes what differs: the observer below sees its own text changes.
    applyBalance() {
      const points = this.balancePoints;
      const text = this.fmt(this.t.page_points, { points });
      document.querySelectorAll('[data-loyalty-steps]').forEach((el) => {
        if (el.textContent !== text) el.textContent = text;
      });
      // Below the cheapest reward: no "Use my Steps" in the cart.
      document.querySelectorAll('[data-loyalty-cart]').forEach((el) => {
        if (!el.hidden && points < Number(el.dataset.minPoints)) el.hidden = true;
      });
    }

    // The cart drawer / page and the header re-render (Section Rendering API) with the metafield.
    watchBalance() {
      if (this.balanceObserver) return;
      this.balanceObserver = new MutationObserver(() => this.applyBalance());
      this.balanceObserver.observe(document.body, { childList: true, subtree: true });
    }

    // A hung app proxy gives up after ME_TIMEOUT instead of leaving "Loading…" up.
    async fetchMe() {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), ME_TIMEOUT);
      try {
        const res = await fetch(this.dataset.endpoint, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
      } finally {
        clearTimeout(timer);
      }
    }

    // Closed and reopened while /me is still running: wait for that request, don't start a second one.
    load() {
      if (!this.loadRequest) {
        this.loadRequest = this.loadOnce().finally(() => {
          this.loadRequest = null;
        });
      }
      return this.loadRequest;
    }

    async loadOnce() {
      try {
        const data = await this.fetchMe();
        // Welcome bonus given by this very request: shown until the panel closes.
        if (data.welcome && data.welcome.points > 0) this.welcome = data.welcome;
        this.render(data);
        this.loaded = true;
      } catch (err) {
        this.renderOffline();
      }
      this.scrollToTarget();
    }

    // /me failed: the balance and level from the synced metafields (data-synced-*), without
    // rewards, codes or history. Customers who aren't members yet (no synced level) get the error.
    renderOffline() {
      const { syncedTier: tier, syncedPoints, syncedPending, pointsPerReward } = this.dataset;
      if (!tier) {
        this.member.replaceChildren(this.el('p', 'loyalty-launcher__error', this.t.error));
        return;
      }
      // A redeem earlier this session (restoreBalance) is newer than the metafield.
      const points = this.balancePoints != null ? this.balancePoints : Number(syncedPoints) || 0;
      const perReward = Number(pointsPerReward) || 0;
      const balance = this.balanceBlock({
        points,
        pointsValue: perReward ? Math.round((Math.max(0, points) / perReward) * 100) / 100 : null,
        pending: Number(syncedPending) || 0,
      });
      const level = this.section('loyalty-launcher__level', this.t.level);
      level.append(this.el('p', 'loyalty-launcher__level-name', this.t[tier] || tier));
      const note = this.section('loyalty-launcher__offline');
      note.append(this.el('p', 'loyalty-launcher__error', this.t.error_partial));
      this.member.replaceChildren(balance, level, note);
    }

    // Background reload after a redeem: keeps what's on screen if it fails.
    async refresh() {
      try {
        this.render(await this.fetchMe());
      } catch (err) {
        /* keep the current view */
      }
    }

    onAction(e) {
      const btn = e.target.closest('[data-loyalty-action]');
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      const { loyaltyAction: action, rewardId, code } = btn.dataset;
      if (action === 'redeem') this.openConfirm(rewardId);
      else if (action === 'cancel') this.cancelConfirm(rewardId);
      else if (action === 'confirm') this.redeem();
      else if (action === 'copy') this.copy(code, btn);
      else if (action === 'share') this.share();
      else if (action === 'apply') this.applyToCart(code);
      else if (action === 'bday-save') this.openBirthdayConfirm();
      else if (action === 'bday-cancel') this.cancelBirthdayConfirm();
      else if (action === 'bday-confirm') this.saveBirthday();
      else if (action === 'profile-save') this.saveProfile();
      else if (action === 'profile-edit') this.editProfile(true);
      else if (action === 'profile-cancel') this.editProfile(false);
      else if (action === 'leave') this.openLeave(true);
      else if (action === 'leave-cancel') this.openLeave(false);
      else if (action === 'leave-confirm') this.leaveClub();
      else if (action === 'leave-emails-off') this.emailsInsteadOfLeaving();
      else if (action === 'emails-toggle') this.toggleEmails();
      // A link: it opens our profile in a new tab as usual; the reward is posted alongside.
      else if (action === 'social') this.follow(btn.dataset.platform);
    }

    openConfirm(rewardId) {
      if (this.state.busy) return;
      Object.assign(this.state, { confirmId: rewardId, key: newKey(), error: null });
      this.render(this.data, '[data-loyalty-action="confirm"]');
    }

    cancelConfirm(rewardId) {
      if (this.state.busy) return;
      Object.assign(this.state, { confirmId: null, key: null, error: null });
      this.render(this.data, `[data-loyalty-action="redeem"][data-reward-id="${CSS.escape(rewardId)}"]`);
    }

    async redeem() {
      const s = this.state;
      if (s.busy || !s.confirmId) return;
      const rewardId = s.confirmId;
      s.busy = true;
      s.error = null;
      this.render();

      const out = await this.postRedeem(rewardId, s.key);
      s.busy = false;
      if (out.body) {
        Object.assign(s, { confirmId: null, key: null, result: out.body });
        this.applyRedeemed(out.body);
        this.saveBalance(out.body.points);
        this.render(this.data, '[data-loyalty-result]');
        this.applyToCart(out.body.code);
        this.refresh();
        return;
      }

      const key = REDEEM_ERRORS[out.error] || 'err_retry';
      s.error = { rewardId, key };
      // "Try again" keeps the confirmation (and its key) open; the rest can't be retried.
      if (key !== 'err_retry') Object.assign(s, { confirmId: null, key: null });
      this.render(this.data, key === 'err_retry' ? '[data-loyalty-action="confirm"]' : '[data-loyalty-error]');
      // Balance / rules may have changed, or the code may exist after a lost response.
      if (out.error !== 'busy' && out.error !== 'login') this.refresh();
    }

    // One click = one idempotency key: network failures and "busy" are retried with it.
    async postRedeem(rewardId, idempotencyKey) {
      for (let attempt = 0; ; attempt += 1) {
        let res;
        try {
          res = await fetch(this.dataset.redeemEndpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ rewardId, idempotencyKey }),
          });
        } catch (err) {
          if (attempt < MAX_RETRIES) {
            await wait(RETRY_DELAY);
            continue;
          }
          return { error: 'network' };
        }
        if (res.status === 409 && attempt < MAX_RETRIES) {
          await wait(RETRY_DELAY);
          continue;
        }
        let body = null;
        try {
          body = await res.json();
        } catch (err) {
          /* not JSON */
        }
        if (res.ok && body && body.code) return { body };
        if (res.status === 401) return { error: 'login' };
        return { error: (body && body.error) || 'unknown' };
      }
    }

    // Until the reload lands: new balance, reward states and the code in "Your codes".
    applyRedeemed(r) {
      const { member } = this.data;
      this.setBalance(r.points);
      member.codes = [this.codeEntry(r), ...(member.codes || []).filter((c) => c.code !== r.code)];
    }

    // New spendable balance (a redeem, a profile / follow reward) until the reload lands.
    setBalance(points) {
      const { program, member } = this.data;
      member.points = points;
      if (program.pointsPerEuroReward) member.pointsValue = Math.round((Math.max(0, points) / program.pointsPerEuroReward) * 100) / 100;
      member.rewards = (member.rewards || []).map((reward) => ({
        ...reward,
        affordable: points >= reward.points,
        missingPoints: Math.max(0, reward.points - points),
      }));
    }

    // Shared by the profile and follow posts: { body } on 2xx, else { error } (server code, "login" or "network").
    async postJson(endpoint, payload) {
      let res;
      try {
        res = await fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch (err) {
        return { error: 'network' };
      }
      let body = null;
      try {
        body = await res.json();
      } catch (err) {
        /* not JSON */
      }
      if (res.ok && body) return { body };
      if (res.status === 401) return { error: 'login' };
      return { error: (body && body.error) || 'unknown' };
    }

    editProfile(editing) {
      if (this.profile.busy) return;
      Object.assign(this.profile, { editing, error: null, awarded: 0 });
      if (editing) this.fillProfileForm(this.data.member.profile);
      this.render(this.data, editing ? '#LoyaltyShoeSize' : '[data-loyalty-action="profile-edit"]');
    }

    async saveProfile() {
      const p = this.profile;
      if (p.busy || !this.profileEl) return;
      const shoeSize = this.profileEl.select.value;
      const styles = this.profileEl.checks.filter((c) => c.checked).map((c) => c.value);
      if (!shoeSize || !styles.length) {
        p.error = 'profile_err';
        this.render(this.data, '[data-loyalty-profile-error]');
        return;
      }
      p.busy = true;
      p.error = null;
      this.render();

      const out = await this.postJson(this.dataset.profileEndpoint, { shoeSize, styles });
      p.busy = false;
      if (out.body && out.body.profile) {
        const m = this.data.member;
        m.profile = { shoeSize: out.body.profile.shoeSize, styles: out.body.profile.styles };
        if (m.earn) m.earn.profile = true;
        if (out.body.awarded > 0) this.setBalance(m.points + out.body.awarded);
        Object.assign(p, { editing: false, awarded: out.body.awarded || 0 });
        this.render(this.data, '[data-loyalty-profile-saved]');
        this.refresh();
        return;
      }
      p.error = out.error === 'login' ? 'err_login' : out.error === 'invalid_profile' ? 'profile_err' : 'err_retry';
      this.render(this.data, '[data-loyalty-profile-error]');
    }

    async follow(platform) {
      const s = this.social;
      if (!SOCIAL_PLATFORMS.includes(platform) || s.busy[platform]) return;
      s.busy[platform] = true;
      s.error[platform] = null;
      const out = await this.postJson(this.dataset.socialEndpoint, { platform });
      s.busy[platform] = false;
      const m = this.data.member;
      if (out.body) {
        if (m.earn) m.earn.social[platform] = true;
        if (out.body.awarded > 0) {
          s.awarded[platform] = out.body.awarded;
          this.setBalance(m.points + out.body.awarded);
        }
        this.render();
        this.refresh();
        return;
      }
      s.error[platform] = out.error === 'login' ? 'err_login' : 'err_retry';
      this.render();
    }

    codeEntry(r) {
      return { code: r.code, rewardId: r.rewardId, value: r.value, minSubtotal: r.minSubtotal, expiresAt: r.expiresAt };
    }

    // The customer's local today as "YYYY-MM-DD" (the server checks Madrid's).
    today() {
      const d = new Date();
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }

    // A real date from 1900 up to today (browsers without a date picker allow typing anything).
    validBirthday(value) {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
      if (!m || value < MIN_BIRTHDAY || value > this.today()) return false;
      const d = new Date(Date.UTC(+m[1], m[2] - 1, +m[3]));
      return d.getUTCMonth() === m[2] - 1 && d.getUTCDate() === +m[3];
    }

    // Kept across re-renders so a half-typed date and its picker state survive them.
    birthdayInput() {
      if (!this.bdayInput) {
        const input = this.el('input', 'loyalty-launcher__bday-input');
        input.type = 'date';
        input.id = 'LoyaltyBirthday';
        input.min = MIN_BIRTHDAY;
        input.autocomplete = 'bday';
        input.dataset.loyaltyBdayInput = '';
        input.addEventListener('input', () => this.clearBirthdayError());
        input.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          this.openBirthdayConfirm();
        });
        this.bdayInput = input;
      }
      this.bdayInput.max = this.today();
      return this.bdayInput;
    }

    // Typing again hides the last error without a re-render (which would move focus).
    clearBirthdayError() {
      if (!this.bday.error || this.bday.confirm) return;
      this.bday.error = null;
      const error = this.member.querySelector('[data-loyalty-bday-error]');
      if (error) error.remove();
      this.bdayInput.removeAttribute('aria-invalid');
      this.bdayInput.removeAttribute('aria-describedby');
    }

    openBirthdayConfirm() {
      const b = this.bday;
      if (b.busy || b.confirm) return;
      const value = this.bdayInput ? this.bdayInput.value : '';
      if (!this.validBirthday(value)) {
        b.error = 'bday_err_invalid';
        this.render(this.data, '[data-loyalty-bday-input]');
        return;
      }
      Object.assign(b, { confirm: value, error: null });
      this.render(this.data, '[data-loyalty-action="bday-confirm"]');
    }

    cancelBirthdayConfirm() {
      if (this.bday.busy) return;
      Object.assign(this.bday, { confirm: null, error: null });
      this.render(this.data, '[data-loyalty-action="bday-save"]');
    }

    async saveBirthday() {
      const b = this.bday;
      if (b.busy || !b.confirm) return;
      b.busy = true;
      b.error = null;
      this.render();

      const out = await this.postBirthday(b.confirm);
      b.busy = false;
      if (out.body) {
        Object.assign(b, { confirm: null, saved: out.body });
        Object.assign(this.data.member, { birthday: out.body.birthday, birthdaySet: true });
        this.render(this.data, '[data-loyalty-bday-saved]');
        return;
      }

      const key = BIRTHDAY_ERRORS[out.error] || 'err_retry';
      b.error = key;
      // "Try again" keeps the confirmation open; the rest can't be retried.
      if (key !== 'err_retry') b.confirm = null;
      this.render(this.data, key === 'err_retry' ? '[data-loyalty-action="bday-confirm"]' : '[data-loyalty-bday-error]');
      // Already saved (another tab, or a lost response): the reload shows the stored date.
      if (out.error !== 'login' && out.error !== 'invalid_date') this.refresh();
    }

    // No automatic retry: if a lost response had saved it, a retry would only say "locked".
    async postBirthday(birthday) {
      let res;
      try {
        res = await fetch(this.dataset.birthdayEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ birthday }),
        });
      } catch (err) {
        return { error: 'network' };
      }
      let body = null;
      try {
        body = await res.json();
      } catch (err) {
        /* not JSON */
      }
      if (res.ok && body && body.birthday) return { body };
      if (res.status === 401) return { error: 'login' };
      return { error: (body && body.error) || 'unknown' };
    }

    async copy(code, btn) {
      try {
        await navigator.clipboard.writeText(code);
      } catch (err) {
        // In-app browsers (Instagram…) often deny the Clipboard API.
        if (!this.copyFallback(code, btn)) return; // the code itself stays selectable
      }
      const label = btn.dataset.copyLabel || this.t.code_copy;
      btn.textContent = this.t.code_copied;
      clearTimeout(btn.copyTimer);
      btn.copyTimer = setTimeout(() => {
        if (btn.isConnected) btn.textContent = label;
      }, 2000);
    }

    copyFallback(code, btn) {
      const area = this.el('textarea', 'loyalty-launcher__copy-buffer');
      area.value = code;
      area.readOnly = true;
      this.panel.append(area);
      area.select();
      area.setSelectionRange(0, code.length);
      let ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (err) {
        /* unsupported */
      }
      area.remove();
      btn.focus();
      return ok;
    }

    referralUrl(code) {
      return `${this.dataset.shopUrl.replace(/\/$/, '')}/?ref=${encodeURIComponent(code)}`;
    }

    async share() {
      const ref = this.data.member.referral;
      try {
        await navigator.share({
          title: this.t.launcher,
          text: this.fmt(this.t.refer_share_text, { amount: this.money(this.data.program.referral.friendDiscount) }),
          url: this.referralUrl(ref.code),
        });
      } catch (err) {
        /* share sheet closed */
      }
    }

    // `discount` replaces the cart's codes, so the ones already there are sent along.
    async applyToCart(code) {
      const s = this.state;
      if (s.cart[code] === 'adding') return;
      s.cart[code] = 'adding';
      this.render();
      const routes = (window.theme && window.theme.routes) || {};
      const opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
      let status = 'failed';
      try {
        const cartRes = await fetch(`${routes.cart_url || '/cart'}.js`, opts);
        if (!cartRes.ok) throw new Error(`HTTP ${cartRes.status}`);
        const cart = await cartRes.json();
        const same = (c) => String(c).toUpperCase() === code.toUpperCase();
        const codes = [...new Set((cart.discount_codes || []).map((d) => d.code).filter((c) => c && !same(c))), code];
        const res = await fetch(`${routes.cart_update_url || '/cart/update'}.js`, {
          ...opts,
          method: 'POST',
          headers: { ...opts.headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const updated = await res.json();
        const applied = (updated.discount_codes || []).find((d) => same(d.code));
        if (applied) {
          // Not applicable yet (cart below the minimum): Shopify keeps it for later.
          status = applied.applicable === false ? 'saved' : 'added';
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (err) {
        /* show the code to type at checkout */
      }
      s.cart[code] = status;
      this.render();
    }

    fmt(template, vars) {
      return String(template || '').replace(/\{(\w+)\}/g, (match, key) => (key in vars ? vars[key] : match));
    }

    money(value) {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(value);
    }

    num(value) {
      return new Intl.NumberFormat(this.locale).format(value);
    }

    date(iso) {
      return new Intl.DateTimeFormat(this.locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso));
    }

    // A calendar date "YYYY-MM-DD" (no time zone shift): "20 October", or "20 October 1990" with the year.
    calendarDate(ymd, withYear) {
      const [y, m, d] = ymd.split('-').map(Number);
      const opts = { day: 'numeric', month: 'long', timeZone: 'UTC' };
      if (withYear) opts.year = 'numeric';
      return new Intl.DateTimeFormat(this.locale, opts).format(new Date(Date.UTC(y, m - 1, d)));
    }

    el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined && text !== null) node.textContent = text;
      return node;
    }

    // A button handled by onAction(): data-loyalty-action + optional reward / code.
    actionButton(text, className, action, extra = {}) {
      const btn = this.el('button', className, text);
      btn.type = 'button';
      btn.dataset.loyaltyAction = action;
      if (extra.rewardId) btn.dataset.rewardId = extra.rewardId;
      if (extra.code) btn.dataset.code = extra.code;
      return btn;
    }

    section(className, title) {
      const box = this.el('section', `loyalty-launcher__section ${className}`);
      if (title) box.append(this.el('h3', 'loyalty-launcher__section-title', title));
      return box;
    }

    // "€5 off · On orders from €100"
    codeSummary(c) {
      return `${this.fmt(this.t.reward_off, { amount: this.money(c.value) })} · ${this.fmt(this.t.reward_min, { amount: this.money(c.minSubtotal) })}`;
    }

    cartStatus(c) {
      const status = this.state.cart[c.code];
      if (!status) return null;
      const text =
        status === 'adding'
          ? this.t.cart_adding
          : status === 'added'
            ? this.t.cart_added
            : status === 'saved'
              ? this.fmt(this.t.cart_saved, { amount: this.money(c.minSubtotal) })
              : this.t.cart_failed;
      return this.el('p', `loyalty-launcher__cart-status is-${status}`, text);
    }

    // Selector of the focused element, to focus its replacement after a re-render.
    focusedAction() {
      const active = document.activeElement;
      if (!active || !(this.member.contains(active) || (this.modalEl && this.modalEl.contains(active)))) return null;
      if ('loyaltyResult' in active.dataset) return '[data-loyalty-result]';
      if ('loyaltyError' in active.dataset) return '[data-loyalty-error]';
      if ('loyaltyBdayInput' in active.dataset) return '[data-loyalty-bday-input]';
      if ('loyaltyBdayError' in active.dataset) return '[data-loyalty-bday-error]';
      if ('loyaltyBdaySaved' in active.dataset) return '[data-loyalty-bday-saved]';
      if ('loyaltyProfileError' in active.dataset) return '[data-loyalty-profile-error]';
      if ('loyaltyProfileSaved' in active.dataset) return '[data-loyalty-profile-saved]';
      // The profile form is reused across renders: its size select / style boxes keep their ids.
      if (active.id && active.closest('[data-loyalty-profile-form]')) return `#${CSS.escape(active.id)}`;
      if (!active.dataset.loyaltyAction) return null;
      let sel = `[data-loyalty-action="${active.dataset.loyaltyAction}"]`;
      if (active.dataset.rewardId) sel += `[data-reward-id="${CSS.escape(active.dataset.rewardId)}"]`;
      if (active.dataset.code) sel += `[data-code="${CSS.escape(active.dataset.code)}"]`;
      if (active.dataset.platform) sel += `[data-platform="${CSS.escape(active.dataset.platform)}"]`;
      return sel;
    }

    render(data = this.data, focusSelector = null) {
      this.data = data;
      if (data.left) {
        this.renderLeaveModal(null);
        this.showNotice('[data-loyalty-left]');
        // Blocked when they left: no way back (/me canRejoin: false).
        const rejoin = this.leftNotice.querySelector('[data-loyalty-rejoin]');
        if (rejoin) rejoin.hidden = data.canRejoin === false;
        return;
      }
      if (data.blocked) {
        this.renderLeaveModal(null);
        this.showNotice('[data-loyalty-blocked]');
        return;
      }
      const m = data.member;
      if (!m) {
        this.member.replaceChildren(this.el('p', 'loyalty-launcher__empty', this.t.empty));
        return;
      }
      // Read before building: the reused birthday input leaves the page as soon as it's appended.
      const keep = focusSelector ? null : this.focusedAction();
      const scrollTop = this.body.scrollTop;
      const t = this.t;
      const s = this.state;
      const canRedeem = data.redeemOpen === true;
      const nodes = [];

      if (this.welcome) {
        const welcome = this.el('p', 'loyalty-launcher__welcome', this.fmt(t.welcome, { points: this.num(this.welcome.points) }));
        welcome.setAttribute('role', 'status');
        nodes.push(welcome);
      }

      nodes.push(this.balanceBlock(m));

      const level = this.section('loyalty-launcher__level', t.level);
      level.append(this.el('p', 'loyalty-launcher__level-name', t[m.tier.key] || m.tier.key));
      if (m.tier.next) {
        const bar = this.el('div', 'loyalty-launcher__progress');
        const fill = this.el('span', 'loyalty-launcher__progress-fill');
        fill.style.width = `${Math.min(100, Math.round((m.tier.spend / m.tier.next.minSpend) * 100))}%`;
        bar.append(fill);
        level.append(bar, this.el('p', 'loyalty-launcher__level-next', this.fmt(t.next_level, { amount: this.money(m.tier.next.missing), tier: t[m.tier.next.key] || m.tier.next.key })));
      } else {
        level.append(this.el('p', 'loyalty-launcher__level-next', t.top_level));
      }
      nodes.push(level);
      nodes.push(this.birthdaySection(data));

      const states = new Map((m.rewards || []).map((r) => [r.id, r]));
      const rewards = this.section('loyalty-launcher__rewards', t.rewards);
      if (s.result) rewards.append(this.resultCard(s.result));
      const list = this.el('ul', 'loyalty-launcher__reward-list');
      list.setAttribute('role', 'list');
      for (const reward of data.program.rewards) {
        const state = states.get(reward.id);
        const ready = !!(state && state.affordable);
        const name = this.fmt(t.reward_off, { amount: this.money(reward.value) });
        const item = this.el('li', `loyalty-launcher__reward${ready ? ' is-ready' : ''}`);
        const text = this.el('div', 'loyalty-launcher__reward-text');
        text.append(
          this.el('span', 'loyalty-launcher__reward-name', name),
          this.el('span', 'loyalty-launcher__reward-min', this.fmt(t.reward_min, { amount: this.money(reward.minSubtotal) })),
        );
        const side = this.el('div', 'loyalty-launcher__reward-side');
        side.append(this.el('span', 'loyalty-launcher__reward-points', `${this.num(reward.points)} Steps`));
        if (canRedeem && ready) {
          if (s.confirmId !== reward.id) {
            const redeem = this.actionButton(t.redeem, 'loyalty-launcher__redeem', 'redeem', { rewardId: reward.id });
            redeem.setAttribute('aria-label', `${t.redeem}: ${name}`);
            if (s.busy) redeem.disabled = true;
            side.append(redeem);
          }
        } else {
          side.append(this.el('span', 'loyalty-launcher__reward-state', ready ? t.reward_ready : this.fmt(t.reward_missing, { points: this.num(state ? state.missingPoints : reward.points) })));
        }
        item.append(text, side);
        if (s.confirmId === reward.id) {
          item.append(this.confirmBlock(reward));
        } else if (s.error && s.error.rewardId === reward.id) {
          const error = this.el('p', 'loyalty-launcher__inline-error', t[s.error.key]);
          error.setAttribute('role', 'alert');
          error.tabIndex = -1;
          error.dataset.loyaltyError = '';
          item.append(error);
        }
        list.append(item);
      }
      rewards.append(list);
      // Reward codes stack with the level order discounts (Shopify: product + order discount).
      if ((data.program.tiers || []).some((tier) => (tier.orderDiscounts || []).length)) {
        rewards.append(this.el('p', 'loyalty-launcher__reward-stack', this.fmt(t.reward_stack, { hype: t.tier2 || 'Hype', grail: t.tier3 || 'Grail' })));
      }
      if (!canRedeem) rewards.append(this.el('p', 'loyalty-launcher__note', t.reward_soon));
      nodes.push(rewards);

      const codes = (m.codes || []).slice();
      if (s.result && !codes.some((c) => c.code === s.result.code)) codes.unshift(this.codeEntry(s.result));
      if (codes.length) {
        const box = this.section('loyalty-launcher__codes', t.codes);
        const rows = this.el('ul', 'loyalty-launcher__code-list');
        rows.setAttribute('role', 'list');
        for (const c of codes) {
          const row = this.el('li', 'loyalty-launcher__code');
          const info = this.el('div', 'loyalty-launcher__code-text');
          info.append(
            this.el('span', 'loyalty-launcher__code-value', c.code),
            this.el('span', 'loyalty-launcher__code-meta', this.codeSummary(c)),
          );
          if (c.expiresAt) info.append(this.el('span', 'loyalty-launcher__code-meta', this.fmt(t.code_expires, { date: this.date(c.expiresAt) })));
          const actions = this.el('div', 'loyalty-launcher__code-actions');
          const apply = this.actionButton(t.code_apply, 'loyalty-launcher__btn', 'apply', { code: c.code });
          if (s.cart[c.code] === 'adding') apply.setAttribute('aria-disabled', 'true');
          actions.append(this.actionButton(t.code_copy, 'loyalty-launcher__btn', 'copy', { code: c.code }), apply);
          row.append(info, actions);
          const status = this.cartStatus(c);
          if (status) row.append(status);
          rows.append(row);
        }
        box.append(rows);
        nodes.push(box);
      }

      const earn = this.earnSection(data);
      if (earn) nodes.push(earn);

      const refer = this.referralSection(data);
      if (refer) nodes.push(refer);

      if (m.history && m.history.length) {
        const activity = this.section('loyalty-launcher__activity', t.activity);
        const rows = this.el('ul', 'loyalty-launcher__activity-list');
        rows.setAttribute('role', 'list');
        for (const entry of m.history) {
          const label = entry.type === 'order' ? this.fmt(t.act_order, { name: entry.orderName || '' }) : t[ACTIVITY_LABELS[entry.type]] || t.act_adjust;
          const row = this.el('li', `loyalty-launcher__activity-row${entry.points < 0 ? ' is-negative' : ''}`);
          const left = this.el('div', 'loyalty-launcher__activity-text');
          left.append(this.el('span', 'loyalty-launcher__activity-label', label));
          const when = entry.availableAt || entry.createdAt;
          left.append(this.el('span', 'loyalty-launcher__activity-date', `${this.date(when)}${entry.status === 'pending' ? ` · ${t.act_pending}` : ''}`));
          row.append(left, this.el('span', 'loyalty-launcher__activity-points', `${entry.points > 0 ? '+' : ''}${this.num(entry.points)}`));
          rows.append(row);
        }
        activity.append(rows);
        nodes.push(activity);
      }

      nodes.push(this.emailsSection(m));
      nodes.push(this.leaveSection());

      this.member.replaceChildren(...nodes);
      this.renderLeaveModal(m);
      this.body.scrollTop = scrollTop;
      // A new target (confirm, result…) is scrolled to; a kept focus stays where the customer is.
      const selector = focusSelector || keep;
      const target = selector && ((this.modalEl && this.modalEl.querySelector(selector)) || this.member.querySelector(selector));
      if (target) target.focus({ preventScroll: !focusSelector });
    }

    // Steps, their worth, Steps on the way and the expiry (render, and renderOffline without expiry).
    balanceBlock(m) {
      const t = this.t;
      const balance = this.el('div', 'loyalty-launcher__balance');
      balance.append(
        this.el('span', 'loyalty-launcher__balance-label', t.balance),
        this.el('span', 'loyalty-launcher__balance-points', `${this.num(m.points)} Steps`),
      );
      if (m.pointsValue != null) balance.append(this.el('span', 'loyalty-launcher__balance-worth', this.fmt(t.worth, { amount: this.money(m.pointsValue) })));
      if (m.pending > 0) {
        balance.append(this.el('span', 'loyalty-launcher__balance-pending', this.fmt(t.pending, { points: this.num(m.pending) })));
        balance.append(this.el('span', 'loyalty-launcher__balance-note', t.pending_note));
      }
      if (m.expiresAt && m.points > 0) {
        balance.append(this.el('span', 'loyalty-launcher__balance-note', this.fmt(t.expires, { date: this.date(m.expiresAt) })));
      }
      return balance;
    }

    // Blocked by next-table (chargeback / breach of the terms) or left the Club (/me sends no
    // member): only the notice Liquid rendered hidden, nothing to redeem, save or share.
    showNotice(selector) {
      const notice = this.querySelector(selector);
      notice.hidden = false;
      this.member.replaceChildren(notice);
    }

    // Club emails on / off: off keeps the membership and the Steps (codes and expiry reminders still come).
    emailsSection(m) {
      const t = this.t;
      const box = this.section('loyalty-launcher__emails', t.emails_title);
      const text = this.el('p', 'loyalty-launcher__emails-text', m.emailsOff ? t.emails_off : t.emails_on);
      text.id = 'LoyaltyEmailsState';
      const btn = this.actionButton(m.emailsOff ? t.emails_turn_on : t.emails_turn_off, 'loyalty-launcher__btn', 'emails-toggle');
      btn.setAttribute('aria-describedby', text.id);
      if (this.emails.busy) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-loading');
      }
      box.append(text, btn);
      if (this.emails.error && !this.leave.confirm) {
        const error = this.el('p', 'loyalty-launcher__inline-error', t[this.emails.error]);
        error.setAttribute('role', 'alert');
        box.append(error);
      }
      return box;
    }

    // { on }: true turns the emails back on. Resolves true when saved.
    async setEmails(on, focusSelector) {
      const e = this.emails;
      if (e.busy) return false;
      Object.assign(e, { busy: true, error: null });
      this.render(this.data, focusSelector);
      const out = await this.postJson(this.dataset.emailsEndpoint, { on });
      e.busy = false;
      if (out.body) {
        this.data.member.emailsOff = !!out.body.emailsOff;
        return true;
      }
      e.error = out.error === 'login' ? 'err_login' : 'err_retry';
      this.render(this.data, focusSelector);
      return false;
    }

    async toggleEmails() {
      const focus = '[data-loyalty-action="emails-toggle"]';
      if (await this.setEmails(!!this.data.member.emailsOff, focus)) this.render(this.data, focus);
    }

    // "Turn off Club emails" in the leave dialog: stay a member, only stop the emails.
    async emailsInsteadOfLeaving() {
      if (!(await this.setEmails(false, '[data-loyalty-action="leave-emails-off"]'))) return;
      this.leave = { confirm: false, busy: false, error: null };
      this.scrollTarget = '.loyalty-launcher__emails';
      this.render(this.data, '[data-loyalty-action="emails-toggle"]');
      this.scrollToTarget();
    }

    // At the very bottom: a plain link; the question is a dialog over the panel (renderLeaveModal).
    leaveSection() {
      const box = this.el('section', 'loyalty-launcher__section loyalty-launcher__leave');
      box.append(this.actionButton(this.t.leave_link, 'loyalty-launcher__leave-link', 'leave'));
      return box;
    }

    // "Leave Next Step Club?": everything that goes, that they can come back from zero,
    // and turning the emails off instead. Rebuilt with every render while open; m null closes it.
    renderLeaveModal(m) {
      if (!this.leave.confirm || !m) {
        if (this.modalEl) this.modalEl.remove();
        this.modalEl = null;
        return;
      }
      const t = this.t;
      if (!this.modalEl) {
        this.modalEl = this.el('div', 'loyalty-launcher__modal');
        // A click on the backdrop is "Cancel"; buttons go through onAction like the panel's.
        this.modalEl.addEventListener('click', (e) => {
          if (e.target === this.modalEl) this.openLeave(false);
          else this.onAction(e);
        });
        this.panel.append(this.modalEl);
      }
      const card = this.el('div', 'loyalty-launcher__modal-card');
      card.setAttribute('role', 'alertdialog');
      card.setAttribute('aria-modal', 'true');
      const title = this.el('h3', 'loyalty-launcher__modal-title', t.leave_title);
      title.id = 'LoyaltyLeaveTitle';
      card.setAttribute('aria-labelledby', title.id);
      const intro = this.el('p', 'loyalty-launcher__modal-text', t.leave_lose);
      intro.id = 'LoyaltyLeaveIntro';
      card.setAttribute('aria-describedby', intro.id);
      const lost = this.el('ul', 'loyalty-launcher__modal-list');
      lost.setAttribute('role', 'list');
      const codes = (m.codes || []).length;
      [
        this.fmt(t.leave_lose_points, { points: this.num(Math.max(0, (m.points || 0) + (m.pending || 0))) }),
        m.tier ? this.fmt(t.leave_lose_level, { tier: t[m.tier.key] || m.tier.key }) : null,
        codes > 0 ? this.fmt(t.leave_lose_codes, { count: this.num(codes) }) : null,
        t.leave_lose_data,
      ]
        .filter(Boolean)
        .forEach((text) => lost.append(this.el('li', null, text)));
      card.append(title, intro, lost, this.el('p', 'loyalty-launcher__modal-text', t.leave_after), this.el('p', 'loyalty-launcher__modal-text', t.leave_rejoin));

      if (!m.emailsOff) {
        const instead = this.el('div', 'loyalty-launcher__modal-instead');
        const off = this.actionButton(t.emails_turn_off_btn, 'loyalty-launcher__btn loyalty-launcher__btn--primary', 'leave-emails-off');
        if (this.emails.busy) {
          off.setAttribute('aria-disabled', 'true');
          off.classList.add('is-loading');
        }
        instead.append(this.el('p', 'loyalty-launcher__modal-text', t.leave_emails_instead), off);
        card.append(instead);
      }

      const busy = this.leave.busy;
      const actions = this.el('div', 'loyalty-launcher__confirm-actions');
      const yes = this.actionButton(t.leave_yes, 'loyalty-launcher__btn loyalty-launcher__btn--danger', 'leave-confirm');
      if (busy) {
        yes.setAttribute('aria-disabled', 'true');
        yes.classList.add('is-loading');
      }
      const no = this.actionButton(t.redeem_cancel, 'loyalty-launcher__btn', 'leave-cancel');
      no.disabled = busy;
      actions.append(yes, no);
      card.append(actions);
      const errorKey = this.leave.error || this.emails.error;
      if (errorKey) {
        const error = this.el('p', 'loyalty-launcher__inline-error', t[errorKey]);
        error.setAttribute('role', 'alert');
        card.append(error);
      }
      this.modalEl.replaceChildren(card);
    }

    openLeave(open) {
      if (this.leave.busy) return;
      this.leave = { confirm: open, busy: false, error: null };
      this.emails.error = null;
      // Focus the safe choice first; closing goes back to the link.
      this.render(this.data, open ? '[data-loyalty-action="leave-cancel"]' : '[data-loyalty-action="leave"]');
    }

    // The "you left" notice (rendered by Liquid, or swapped in when /me says left).
    onRejoinAction(e) {
      const btn = e.target.closest('[data-loyalty-rejoin-action]');
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      const action = btn.dataset.loyaltyRejoinAction;
      if (action === 'open') this.openRejoin(true);
      else if (action === 'cancel') this.openRejoin(false);
      else if (action === 'confirm') this.rejoinClub();
    }

    openRejoin(open) {
      if (this.rejoin.busy) return;
      this.rejoin = { confirm: open, busy: false, error: null };
      this.renderRejoin(open ? '[data-loyalty-rejoin-action="cancel"]' : '[data-loyalty-rejoin-action="open"]');
    }

    // Liquid's note + button, or the question "you start from zero" with Yes / Cancel.
    renderRejoin(focusSelector) {
      const box = this.leftNotice && this.leftNotice.querySelector('[data-loyalty-rejoin]');
      if (!box) return;
      if (!this.rejoinIntro) this.rejoinIntro = [...box.childNodes];
      const r = this.rejoin;
      const t = this.t;
      if (!r.confirm) {
        box.replaceChildren(...this.rejoinIntro);
      } else {
        const confirm = this.el('div', 'loyalty-launcher__confirm');
        const text = this.el('p', 'loyalty-launcher__confirm-text', t.rejoin_confirm);
        text.id = 'LoyaltyRejoinConfirm';
        confirm.setAttribute('role', 'group');
        confirm.setAttribute('aria-labelledby', text.id);
        const actions = this.el('div', 'loyalty-launcher__confirm-actions');
        const yes = this.el('button', 'loyalty-launcher__btn loyalty-launcher__btn--primary', t.rejoin_yes);
        yes.type = 'button';
        yes.dataset.loyaltyRejoinAction = 'confirm';
        if (r.busy) {
          yes.setAttribute('aria-disabled', 'true');
          yes.classList.add('is-loading');
        }
        const no = this.el('button', 'loyalty-launcher__btn', t.redeem_cancel);
        no.type = 'button';
        no.dataset.loyaltyRejoinAction = 'cancel';
        no.disabled = r.busy;
        actions.append(yes, no);
        confirm.append(text, actions);
        if (r.error) {
          const error = this.el('p', 'loyalty-launcher__inline-error', t[r.error]);
          error.setAttribute('role', 'alert');
          confirm.append(error);
        }
        box.replaceChildren(confirm);
      }
      const target = focusSelector && box.querySelector(focusSelector);
      if (target) target.focus();
    }

    async rejoinClub() {
      const r = this.rejoin;
      if (r.busy) return;
      Object.assign(r, { busy: true, error: null });
      this.renderRejoin('[data-loyalty-rejoin-action="confirm"]');
      const out = await this.postJson(this.dataset.rejoinEndpoint, { confirm: true });
      r.busy = false;
      if (!out.body) {
        // busy: next-table is still deleting the old membership (they left moments ago).
        r.error = out.error === 'login' ? 'err_login' : out.error === 'busy' ? 'rejoin_busy' : 'err_retry';
        this.renderRejoin('[data-loyalty-rejoin-action="confirm"]');
        return;
      }
      this.rejoin = { confirm: false, busy: false, error: null };
      this.renderRejoin();
      // The notice goes back where Liquid put it (hidden), and the panel loads the new membership.
      this.leftNotice.hidden = true;
      this.body.append(this.leftNotice);
      if (!this.member) {
        this.member = this.el('div', 'loyalty-launcher__member');
        this.member.dataset.loyaltyMember = '';
        this.member.setAttribute('aria-live', 'polite');
        this.member.addEventListener('click', (e) => this.onAction(e));
        this.body.prepend(this.member);
      }
      this.member.replaceChildren(this.el('p', 'loyalty-launcher__loading', this.t.loading));
      this.data = null;
      this.loaded = false;
      this.panel.querySelector('[data-loyalty-close]').focus();
      await this.load();
    }

    async leaveClub() {
      if (this.leave.busy) return;
      Object.assign(this.leave, { busy: true, error: null });
      this.render(this.data, '[data-loyalty-action="leave-confirm"]');
      const out = await this.postJson(this.dataset.leaveEndpoint, { confirm: true });
      this.leave.busy = false;
      if (!out.body) {
        this.leave.error = out.error === 'login' ? 'err_login' : 'err_retry';
        this.render(this.data, '[data-loyalty-action="leave-confirm"]');
        return;
      }
      this.leave.confirm = false;
      // Nothing of the Club stays on the page: the Steps count, the cart's "Use my Steps", the bubble.
      try {
        window.sessionStorage.removeItem(BALANCE_KEY);
      } catch (err) {
        /* nothing saved */
      }
      document.querySelectorAll('[data-loyalty-steps], [data-loyalty-cart], [data-loyalty-bubble], [data-loyalty-bubble-dot]').forEach((el) => {
        el.hidden = true;
      });
      this.render({ ...this.data, left: true, canRejoin: true, member: null });
      // The dialog is gone with the focused button: on to "Rejoin" in the notice.
      const rejoin = this.leftNotice.querySelector('[data-loyalty-rejoin-action="open"]');
      (rejoin && !rejoin.closest('[hidden]') ? rejoin : this.panel.querySelector('[data-loyalty-close]')).focus();
    }

    confirmBlock(reward) {
      const t = this.t;
      const busy = this.state.busy;
      const box = this.el('div', 'loyalty-launcher__confirm');
      const days = (this.data.program && this.data.program.codeValidDays) || 60;
      const message = this.el(
        'p',
        'loyalty-launcher__confirm-text',
        this.fmt(t.redeem_confirm, { points: this.num(reward.points), amount: this.money(reward.value), min: this.money(reward.minSubtotal), days }),
      );
      message.id = `LoyaltyConfirm-${reward.id}`;
      box.setAttribute('role', 'group');
      box.setAttribute('aria-labelledby', message.id);
      const actions = this.el('div', 'loyalty-launcher__confirm-actions');
      const yes = this.actionButton(busy ? t.redeem_busy : t.redeem_yes, 'loyalty-launcher__btn loyalty-launcher__btn--primary', 'confirm');
      if (busy) {
        // aria-disabled (not disabled) so focus stays on it while the request runs.
        yes.setAttribute('aria-disabled', 'true');
        yes.classList.add('is-loading');
      }
      const no = this.actionButton(t.redeem_cancel, 'loyalty-launcher__btn', 'cancel', { rewardId: reward.id });
      no.disabled = busy;
      actions.append(yes, no);
      box.append(message, actions);
      if (this.state.error && this.state.error.rewardId === reward.id) {
        const error = this.el('p', 'loyalty-launcher__inline-error', t[this.state.error.key]);
        error.setAttribute('role', 'alert');
        box.append(error);
      }
      return box;
    }

    resultCard(r) {
      const t = this.t;
      const card = this.el('div', 'loyalty-launcher__result');
      const title = this.el('p', 'loyalty-launcher__result-title', t.redeem_done);
      title.tabIndex = -1;
      title.dataset.loyaltyResult = '';
      card.append(
        title,
        this.el('p', 'loyalty-launcher__result-code', r.code),
        this.el('p', 'loyalty-launcher__code-meta', this.codeSummary(r)),
      );
      if (r.expiresAt) card.append(this.el('p', 'loyalty-launcher__code-meta', this.fmt(t.code_expires, { date: this.date(r.expiresAt) })));
      card.append(this.actionButton(t.code_copy, 'loyalty-launcher__btn loyalty-launcher__btn--primary', 'copy', { code: r.code }));
      const status = this.cartStatus(r);
      if (status) {
        status.setAttribute('aria-live', 'polite');
        card.append(status);
      }
      return card;
    }

    // Only when the program is open to this member (member.earn is left out otherwise).
    earnSection(data) {
      const m = data.member;
      const earn = m.earn;
      if (!earn) return null;
      const t = this.t;
      const p = data.program;
      const box = this.section('loyalty-launcher__earn', t.earn_title);
      const list = this.el('ul', 'loyalty-launcher__reward-list');
      list.setAttribute('role', 'list');

      if (p.signupPoints > 0) {
        list.append(this.earnRow({ name: t.earn_signup, note: earn.signup ? null : t.earn_signup_wait, points: p.signupPoints, done: earn.signup }));
      }

      if (p.profilePoints > 0 || m.profile) {
        const row = this.earnRow({ name: t.profile_title, note: earn.profile ? null : t.profile_intro, points: p.profilePoints, done: earn.profile });
        row.append(this.profileBlock(m));
        list.append(row);
      }

      for (const platform of SOCIAL_PLATFORMS) {
        const url = this.dataset[`${platform}Url`];
        if (!url || !(p.socialPoints > 0)) continue;
        const done = !!earn.social[platform];
        const link = this.el('a', 'loyalty-launcher__btn', t.social_follow);
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.dataset.loyaltyAction = 'social';
        link.dataset.platform = platform;
        link.setAttribute('aria-label', t[`social_${platform}`]);
        const row = this.earnRow({ name: t[`social_${platform}`], points: p.socialPoints, done, action: done ? null : link });
        const awarded = this.social.awarded[platform];
        if (awarded) row.append(this.el('p', 'loyalty-launcher__earn-awarded', this.fmt(t.earn_points, { points: this.num(awarded) })));
        if (this.social.error[platform]) {
          const error = this.el('p', 'loyalty-launcher__inline-error', t[this.social.error[platform]]);
          error.setAttribute('role', 'alert');
          row.append(error);
        }
        list.append(row);
      }

      if (!list.children.length) return null;
      box.append(list);
      return box;
    }

    // "Name · note | +50 Steps [action]" or "Done", in the rewards list style.
    earnRow({ name, note, points, done, action }) {
      const item = this.el('li', `loyalty-launcher__reward loyalty-launcher__earn-item${done ? ' is-ready' : ''}`);
      const text = this.el('div', 'loyalty-launcher__reward-text');
      text.append(this.el('span', 'loyalty-launcher__reward-name', name));
      if (note) text.append(this.el('span', 'loyalty-launcher__reward-min', note));
      const side = this.el('div', 'loyalty-launcher__reward-side');
      side.append(this.el('span', done ? 'loyalty-launcher__reward-state' : 'loyalty-launcher__reward-points', done ? this.t.earn_done : this.fmt(this.t.earn_points, { points: this.num(points) })));
      if (action) side.append(action);
      item.append(text, side);
      return item;
    }

    // Saved and not editing: the summary + Edit. Otherwise the form (reused across renders).
    profileBlock(m) {
      const t = this.t;
      const p = this.profile;
      const box = this.el('div', 'loyalty-launcher__profile');
      if (m.profile && !p.editing) {
        const saved = this.el('p', 'loyalty-launcher__profile-value', this.profileText(m.profile));
        saved.tabIndex = -1;
        saved.dataset.loyaltyProfileSaved = '';
        box.append(saved);
        if (p.awarded > 0) box.append(this.el('p', 'loyalty-launcher__earn-awarded', this.fmt(t.earn_points, { points: this.num(p.awarded) })));
        box.append(this.actionButton(t.profile_edit, 'loyalty-launcher__btn', 'profile-edit'));
        return box;
      }
      box.append(this.profileForm(!!m.profile));
      return box;
    }

    // "EU 42.5 · Sneakers, Streetwear"
    profileText(profile) {
      const styles = (profile.styles || []).map((s) => this.t[`style_${s}`] || s).join(', ');
      return this.fmt(this.t.profile_summary, { size: this.num(Number(profile.shoeSize)), styles });
    }

    profileForm(canCancel) {
      const t = this.t;
      if (!this.profileEl) {
        const form = this.el('div', 'loyalty-launcher__profile-form');
        form.dataset.loyaltyProfileForm = '';
        const sizeLabel = this.el('label', 'loyalty-launcher__field-label', t.profile_size);
        const select = this.el('select', 'loyalty-launcher__select');
        select.id = 'LoyaltyShoeSize';
        sizeLabel.htmlFor = select.id;
        select.append(new Option(t.profile_size_pick, ''));
        for (const size of SHOE_SIZES) select.append(new Option(this.num(Number(size)), size));
        const styles = this.el('fieldset', 'loyalty-launcher__styles');
        styles.append(this.el('legend', 'loyalty-launcher__field-label', t.profile_styles));
        const chips = this.el('div', 'loyalty-launcher__chips');
        const checks = PROFILE_STYLES.map((style) => {
          const chip = this.el('label', 'loyalty-launcher__chip');
          const box = this.el('input', 'loyalty-launcher__chip-input');
          box.type = 'checkbox';
          box.value = style;
          box.id = `LoyaltyStyle-${style}`;
          chip.append(box, this.el('span', 'loyalty-launcher__chip-text', t[`style_${style}`] || style));
          chips.append(chip);
          return box;
        });
        styles.append(chips);
        const actions = this.el('div', 'loyalty-launcher__confirm-actions');
        const save = this.actionButton(t.bday_save, 'loyalty-launcher__btn loyalty-launcher__btn--primary', 'profile-save');
        const cancel = this.actionButton(t.redeem_cancel, 'loyalty-launcher__btn', 'profile-cancel');
        actions.append(save, cancel);
        const error = this.el('p', 'loyalty-launcher__inline-error');
        error.id = 'LoyaltyProfile-Error';
        error.setAttribute('role', 'alert');
        error.tabIndex = -1;
        error.dataset.loyaltyProfileError = '';
        // Changing anything hides the last error without a re-render (which would move focus).
        form.addEventListener('change', () => {
          if (!this.profile.error) return;
          this.profile.error = null;
          error.hidden = true;
        });
        form.append(sizeLabel, select, styles, actions, error);
        this.profileEl = { form, select, checks, save, cancel, error };
      }
      const el = this.profileEl;
      const p = this.profile;
      el.cancel.hidden = !canCancel;
      el.cancel.disabled = p.busy;
      el.save.textContent = p.busy ? t.bday_busy : t.bday_save;
      el.save.classList.toggle('is-loading', p.busy);
      // aria-disabled (not disabled) so focus stays on it while the request runs.
      if (p.busy) el.save.setAttribute('aria-disabled', 'true');
      else el.save.removeAttribute('aria-disabled');
      el.error.textContent = p.error ? t[p.error] : '';
      el.error.hidden = !p.error;
      return el.form;
    }

    fillProfileForm(profile) {
      if (!this.profileEl) this.profileForm(true);
      this.profileEl.select.value = (profile && profile.shoeSize) || '';
      const styles = (profile && profile.styles) || [];
      this.profileEl.checks.forEach((box) => {
        box.checked = styles.includes(box.value);
      });
    }

    // Only when the program is open to this member (member.referral is left out otherwise).
    referralSection(data) {
      const ref = data.member.referral;
      const rules = data.program.referral;
      if (!ref || !rules) return null;
      const t = this.t;
      const url = this.referralUrl(ref.code);
      const box = this.section('loyalty-launcher__refer', t.refer_title);
      box.append(
        this.el(
          'p',
          'loyalty-launcher__refer-text',
          this.fmt(t.refer_text, { friend: this.money(rules.friendDiscount), min: this.money(rules.friendMinSubtotal), points: this.num(rules.referrerPoints) }),
        ),
      );
      const link = this.el('div', 'loyalty-launcher__refer-link');
      // Wraps before "?ref=" rather than inside the code.
      const shown = this.el('p', 'loyalty-launcher__refer-url');
      const cut = url.indexOf('?');
      shown.append(url.slice(0, cut), document.createElement('wbr'), this.el('span', 'loyalty-launcher__refer-query', url.slice(cut)));
      link.append(this.el('p', 'loyalty-launcher__refer-label', t.refer_link_label), shown);
      const actions = this.el('div', 'loyalty-launcher__refer-actions');
      const copy = this.actionButton(t.refer_copy, 'loyalty-launcher__btn loyalty-launcher__btn--primary', 'copy', { code: url });
      copy.dataset.copyLabel = t.refer_copy;
      actions.append(copy);
      if (navigator.share) actions.append(this.actionButton(t.refer_share, 'loyalty-launcher__btn', 'share'));
      link.append(actions);
      box.append(link);
      if (ref.rewarded > 0 || ref.waiting > 0) {
        const stats = this.el('dl', 'loyalty-launcher__refer-stats');
        for (const [value, label] of [
          [ref.rewarded, t.refer_rewarded],
          [ref.waiting, t.refer_waiting],
        ]) {
          const stat = this.el('div', 'loyalty-launcher__refer-stat');
          stat.append(this.el('dt', 'loyalty-launcher__refer-stat-label', label), this.el('dd', 'loyalty-launcher__refer-stat-value', this.num(value)));
          stats.append(stat);
        }
        box.append(stats);
      }
      return box;
    }

    // Not saved yet: form → in-panel confirmation. Saved: only the day and month, never the year.
    birthdaySection(data) {
      const t = this.t;
      const m = data.member;
      const b = this.bday;
      const box = this.section('loyalty-launcher__birthday', t.bday_title);

      if (b.saved) {
        const saved = this.el('p', 'loyalty-launcher__bday-value', this.fmt(t.bday_saved, { date: this.calendarDate(b.saved.birthday) }));
        saved.tabIndex = -1;
        saved.dataset.loyaltyBdaySaved = '';
        box.append(saved);
        if (b.saved.firstRewardOn) {
          box.append(this.el('p', 'loyalty-launcher__bday-note', this.fmt(t.bday_first, { date: this.calendarDate(b.saved.firstRewardOn, true) })));
        }
        return box;
      }
      if (m.birthdaySet) {
        if (m.birthday) box.append(this.el('p', 'loyalty-launcher__bday-value', this.fmt(t.bday_set, { date: this.calendarDate(m.birthday) })));
        box.append(this.el('p', 'loyalty-launcher__bday-note', t.bday_contact));
        return box;
      }

      // The points are a level perk: promised only once perks are visible to this customer.
      const tier = (data.program.tiers || []).find((x) => x.key === m.tier.key);
      const points = m.perksVisible && tier ? tier.birthdayPoints : 0;
      box.append(this.el('p', 'loyalty-launcher__bday-intro', points > 0 ? this.fmt(t.bday_intro, { points: this.num(points) }) : t.bday_intro_plain));

      const input = this.birthdayInput();
      input.disabled = !!b.confirm;
      const label = this.el('label', 'loyalty-launcher__bday-label', t.bday_label);
      label.htmlFor = input.id;
      const row = this.el('div', 'loyalty-launcher__bday-row');
      row.append(input);
      if (!b.confirm) row.append(this.actionButton(t.bday_save, 'loyalty-launcher__btn loyalty-launcher__btn--primary', 'bday-save'));
      box.append(label, row);

      const error = b.error ? this.el('p', 'loyalty-launcher__inline-error', t[b.error]) : null;
      if (error) {
        error.id = 'LoyaltyBirthday-Error';
        error.setAttribute('role', 'alert');
        error.tabIndex = -1;
        error.dataset.loyaltyBdayError = '';
      }
      const invalid = b.error === 'bday_err_invalid' && !b.confirm;
      if (invalid) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', error.id);
      } else {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      }

      if (b.confirm) box.append(this.birthdayConfirm(error));
      else if (error) box.append(error);
      return box;
    }

    birthdayConfirm(error) {
      const t = this.t;
      const busy = this.bday.busy;
      const box = this.el('div', 'loyalty-launcher__confirm');
      const message = this.el('p', 'loyalty-launcher__confirm-text', this.fmt(t.bday_confirm, { date: this.calendarDate(this.bday.confirm, true) }));
      message.id = 'LoyaltyBirthday-Confirm';
      box.setAttribute('role', 'group');
      box.setAttribute('aria-labelledby', message.id);
      const actions = this.el('div', 'loyalty-launcher__confirm-actions');
      const yes = this.actionButton(busy ? t.bday_busy : t.redeem_yes, 'loyalty-launcher__btn loyalty-launcher__btn--primary', 'bday-confirm');
      if (busy) {
        // aria-disabled (not disabled) so focus stays on it while the request runs.
        yes.setAttribute('aria-disabled', 'true');
        yes.classList.add('is-loading');
      }
      const no = this.actionButton(t.redeem_cancel, 'loyalty-launcher__btn', 'bday-cancel');
      no.disabled = busy;
      actions.append(yes, no);
      box.append(message, actions);
      if (error) box.append(error);
      return box;
    }
  }
  customElements.define('loyalty-panel', LoyaltyPanel);
}
